import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loader2, SlidersHorizontal, ChevronDown, ChevronRight, CheckCircle2, XCircle, Ban, History, AlertTriangle } from 'lucide-react';
import baseURL from '../../utils/baseURL';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import {
    FilterPanel, FilterSection, FilterOptionList, FilterPopover, FilterSearchableList,
} from '../../components/common/FilterPanel';
import { getTenantOptions } from '../../redux/tenantSlice';
import { formatDuration } from './aiUsage';
import { formatTime } from '../../utils/clock';

// Super Admin > Audit & Security > AI Log — the usage table under the three
// status cards (owner, 2 Oct 2026): ONE ROW PER AI USE, newest first, from
// GET /audit/ai-usage. Who is a person's NAME and role (never a system id,
// owner rule 29 Sep 2026); where is the organisation's name.

// What the server writes today (services/aiUsageLog.js#AI_USAGE_KINDS). The
// response carries the live list; this is the fallback before it arrives.
const AI_USAGE_KINDS = [
    'summary', 'overall_summary', 'translation', 'transcription', 'ocr', 'meeting_transcription',
    'assistant', 'assistant_voice', 'report', 'prescription_ocr', 'radiology_dictation', 'lab_summary',
];
const STATUSES = ['success', 'failed', 'refused'];

const STATUS_STYLE = {
    success: { badge: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    failed: { badge: 'bg-red-100 text-red-700', icon: XCircle },
    refused: { badge: 'bg-amber-100 text-amber-700', icon: Ban },
};
const roleColor = (r) => ({
    admin: 'bg-violet-100 text-violet-700',
    super_admin: 'bg-violet-100 text-violet-700',
    doctor: 'bg-[#4ABDBD]/15 text-[#2D8A8A]',
    patient: 'bg-blue-100 text-blue-700',
}[r] || 'bg-gray-100 text-gray-600');

const EMPTY = { tenantId: '', kind: [], status: [], dateFrom: '', dateTo: '' };
const activeCount = (f) => (f.tenantId ? 1 : 0) + f.kind.length + f.status.length + (f.dateFrom ? 1 : 0) + (f.dateTo ? 1 : 0);

export default function AiUsageLog({ refreshKey = 0 }) {
    const { t, i18n } = useTranslation();
    const axiosInstance = useAxiosAuth();
    const dispatch = useDispatch();
    const tenantOptions = useSelector((st) => st.tenant?.tenantOptions);
    useEffect(() => { dispatch(getTenantOptions()); }, [dispatch]);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [kinds, setKinds] = useState(AI_USAGE_KINDS);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [filters, setFilters] = useState(EMPTY);
    const [tempFilters, setTempFilters] = useState(EMPTY);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const filterBtnRef = useRef(null);
    const filterRef = useRef(null);

    useEffect(() => { setPage(1); }, [filters, pageSize]);
    useEffect(() => { if (isFilterOpen) setTempFilters(filters); }, [isFilterOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close the popover on an outside click, dropping unapplied changes.
    useEffect(() => {
        const onDown = (e) => {
            if (isFilterOpen && filterRef.current && !filterRef.current.contains(e.target)
                && filterBtnRef.current && !filterBtnRef.current.contains(e.target)) {
                setTempFilters(filters);
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [isFilterOpen, filters]);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            setFailed(false);
            try {
                const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
                if (filters.tenantId) params.append('tenantId', filters.tenantId);
                if (filters.kind.length) params.append('kind', filters.kind.join(','));
                if (filters.status.length) params.append('status', filters.status.join(','));
                // A date-only pick means the WHOLE local day at either end.
                if (filters.dateFrom) { const d = new Date(`${filters.dateFrom}T00:00:00`); params.append('startDate', d.toISOString()); }
                if (filters.dateTo) { const d = new Date(`${filters.dateTo}T23:59:59.999`); params.append('endDate', d.toISOString()); }
                const res = await axiosInstance.get(`${baseURL}/audit/ai-usage?${params.toString()}`);
                if (!alive) return;
                setRows(res.data?.rows || []);
                setTotal(res.data?.total || 0);
                if (Array.isArray(res.data?.kinds) && res.data.kinds.length) setKinds(res.data.kinds);
            } catch {
                if (alive) { setRows([]); setTotal(0); setFailed(true); }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, filters, refreshKey]);

    const pages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);
    const nActive = activeCount(filters);
    const kindLabel = (k) => t(`aiUsage.kind.${k}`, { defaultValue: k });
    const roleLabel = (r) => (r ? t(`aiUsage.role.${r}`, { defaultValue: r }) : '');
    const kindOptions = kinds.map((k) => ({ value: k, label: kindLabel(k) }));
    const statusOptions = STATUSES.map((s) => ({ value: s, label: t(`aiUsage.status.${s}`) }));
    // The date in the screen's language, the time of day 12-hour (utils/clock.js).
    const fmtTime = (d) => {
        if (!d) return '—';
        const x = new Date(d);
        return Number.isNaN(x.getTime()) ? '—' : `${x.toLocaleDateString(i18n.language || undefined)}, ${formatTime(x)}`;
    };

    return (
        <section className="mt-6" data-testid="ai-usage-section">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <History className="w-5 h-5 text-[#4ABDBD]" /> {t('aiUsage.title')}
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">{t('aiUsage.subtitle')}</p>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400" data-testid="ai-usage-total">{t('aiUsage.entries', { count: total })}</span>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        aria-label={t('aiUsage.page_size')}
                        data-testid="ai-usage-page-size"
                        className="tt-select px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                    >
                        {[25, 50, 100].map((n) => <option key={n} value={n}>{t('aiUsage.per_page', { count: n })}</option>)}
                    </select>
                    <div className="relative">
                        <button
                            ref={filterBtnRef}
                            type="button"
                            data-testid="ai-usage-filter-button"
                            aria-label={t('aiUsage.filters')}
                            onClick={() => setIsFilterOpen((o) => !o)}
                            className="relative flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow hover:shadow-lg transition-all font-semibold text-sm cursor-pointer"
                        >
                            <SlidersHorizontal className="w-4 h-4" />
                            {t('aiUsage.filters')}
                            {nActive > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 bg-white text-[#2D8A8A] text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow" data-testid="ai-usage-filter-count">{nActive}</span>
                            )}
                        </button>
                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => { setTempFilters(EMPTY); setFilters(EMPTY); setIsFilterOpen(false); }}
                                    onApply={() => { setFilters(tempFilters); setIsFilterOpen(false); }}
                                    clearLabel={t('aiUsage.clear')}
                                    applyLabel={t('aiUsage.apply')}
                                >
                                    <FilterSection title={t('aiUsage.filter_org')} isActive={!!tempFilters.tenantId}>
                                        <FilterSearchableList
                                            items={tenantOptions || []}
                                            valueKey="tenantId"
                                            labelKey="name"
                                            sublabelKey="__none__"
                                            value={(tenantOptions || []).find((x) => x.tenantId === tempFilters.tenantId) || null}
                                            onChange={(item) => setTempFilters({ ...tempFilters, tenantId: item?.tenantId || '' })}
                                            allLabel={t('aiUsage.all_orgs')}
                                            searchPlaceholder={t('aiUsage.search_orgs')}
                                        />
                                    </FilterSection>
                                    <FilterSection title={t('aiUsage.filter_kind')} isActive={tempFilters.kind.length > 0}>
                                        <FilterOptionList multiple value={tempFilters.kind} onChange={(v) => setTempFilters({ ...tempFilters, kind: v })} options={kindOptions} />
                                    </FilterSection>
                                    <FilterSection title={t('aiUsage.filter_status')} isActive={tempFilters.status.length > 0}>
                                        <FilterOptionList multiple value={tempFilters.status} onChange={(v) => setTempFilters({ ...tempFilters, status: v })} options={statusOptions} />
                                    </FilterSection>
                                    <FilterSection title={t('aiUsage.filter_dates')} isActive={!!(tempFilters.dateFrom || tempFilters.dateTo)}>
                                        <div className="flex flex-col gap-2 px-1 py-1">
                                            <label className="text-xs text-gray-500">{t('aiUsage.from')}
                                                <input type="date" data-testid="ai-usage-date-from" value={tempFilters.dateFrom} onChange={(e) => setTempFilters({ ...tempFilters, dateFrom: e.target.value })}
                                                    className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                                            </label>
                                            <label className="text-xs text-gray-500">{t('aiUsage.to')}
                                                <input type="date" data-testid="ai-usage-date-to" value={tempFilters.dateTo} onChange={(e) => setTempFilters({ ...tempFilters, dateTo: e.target.value })}
                                                    className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
                                            </label>
                                        </div>
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-4 sm:p-5 border border-gray-100">
                {loading ? (
                    <div className="flex items-center justify-center py-14" data-testid="ai-usage-loading">
                        <Loader2 className="animate-spin text-[#4ABDBD]" size={26} />
                    </div>
                ) : failed ? (
                    <div className="py-10 text-center" data-testid="ai-usage-error">
                        <AlertTriangle className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">{t('aiUsage.load_failed')}</p>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="py-12 text-center" data-testid="ai-usage-empty">
                        <div className="w-12 h-12 rounded-2xl bg-[#4ABDBD]/10 text-[#2D8A8A] flex items-center justify-center mx-auto mb-3">
                            <History className="w-6 h-6" />
                        </div>
                        <p className="text-sm font-medium text-slate-600">{t('aiUsage.empty')}</p>
                        <p className="text-xs text-slate-400 mt-1">{nActive > 0 ? t('aiUsage.empty_filtered') : t('aiUsage.empty_hint')}</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto custom-scrollbar">
                            <table className="w-full text-sm whitespace-nowrap" data-testid="ai-usage-table">
                                <thead>
                                    <tr className="text-left text-xs text-slate-400 border-b border-gray-100">
                                        <th className="py-2 pr-2 w-6"></th>
                                        <th className="py-2 pr-3 font-semibold">{t('aiUsage.col_time')}</th>
                                        <th className="py-2 pr-3 font-semibold">{t('aiUsage.col_org')}</th>
                                        <th className="py-2 pr-3 font-semibold">{t('aiUsage.col_who')}</th>
                                        <th className="py-2 pr-3 font-semibold">{t('aiUsage.col_kind')}</th>
                                        <th className="py-2 pr-3 font-semibold">{t('aiUsage.col_status')}</th>
                                        <th className="py-2 font-semibold text-right">{t('aiUsage.col_duration')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => {
                                        const style = STATUS_STYLE[r.status] || STATUS_STYLE.failed;
                                        const StatusIcon = style.icon;
                                        const hasReason = !!r.reason;
                                        const open = expanded === r.id;
                                        return (
                                            <React.Fragment key={r.id}>
                                                <tr
                                                    className={`border-b border-gray-50 ${hasReason ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
                                                    data-testid={`ai-usage-row-${r.id}`}
                                                    onClick={hasReason ? () => setExpanded(open ? null : r.id) : undefined}
                                                >
                                                    <td className="py-2.5 pr-2 text-gray-400">
                                                        {hasReason && (open ? <ChevronDown size={15} /> : <ChevronRight size={15} />)}
                                                    </td>
                                                    <td className="py-2.5 pr-3 text-gray-600">{fmtTime(r.createdAt)}</td>
                                                    <td className="py-2.5 pr-3 text-gray-700">
                                                        <div className="max-w-[200px] truncate" title={r.organisationName || ''}>
                                                            {r.organisationName || (r.tenantId ? '—' : t('aiUsage.platform'))}
                                                        </div>
                                                    </td>
                                                    <td className="py-2.5 pr-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-800 font-medium">{r.actorName || '—'}</span>
                                                            {r.actorRole && (
                                                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${roleColor(r.actorRole)}`}>{roleLabel(r.actorRole)}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-2.5 pr-3 text-gray-700">{kindLabel(r.kind)}</td>
                                                    <td className="py-2.5 pr-3">
                                                        <span
                                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${style.badge}`}
                                                            title={r.reason || undefined}
                                                            data-testid={`ai-usage-status-${r.id}`}
                                                        >
                                                            <StatusIcon className="w-3.5 h-3.5" />
                                                            {t(`aiUsage.status.${r.status}`, { defaultValue: r.status })}
                                                        </span>
                                                    </td>
                                                    <td className="py-2.5 text-right text-gray-500 tabular-nums">{formatDuration(r.durationMs, t)}</td>
                                                </tr>
                                                {open && (
                                                    <tr className="bg-gray-50/70" data-testid={`ai-usage-reason-${r.id}`}>
                                                        <td colSpan={7} className="px-4 py-2.5 whitespace-normal break-words text-xs">
                                                            <span className="text-slate-400">{t('aiUsage.reason')}: </span>
                                                            <span className="text-gray-700">{r.reason}</span>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex items-center justify-between mt-4 text-sm">
                            <span className="text-slate-400" data-testid="ai-usage-page">{t('aiUsage.page_of', { page, pages })}</span>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    data-testid="ai-usage-prev"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    className={`px-3 py-1.5 rounded-lg border ${page <= 1 ? 'text-gray-300 border-gray-100' : 'text-gray-600 border-gray-300 hover:bg-gray-50 cursor-pointer'}`}
                                >
                                    {t('aiUsage.prev')}
                                </button>
                                <button
                                    type="button"
                                    data-testid="ai-usage-next"
                                    disabled={page >= pages}
                                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                                    className={`px-3 py-1.5 rounded-lg border ${page >= pages ? 'text-gray-300 border-gray-100' : 'text-gray-600 border-gray-300 hover:bg-gray-50 cursor-pointer'}`}
                                >
                                    {t('aiUsage.next')}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
